import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import {
  Bell,
  BellOff,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Droplets,
  TrendingDown,
  Wrench,
  Calendar,
  Settings,
  X,
  AlertTriangle,
  Volume2,
  VolumeX,
  RefreshCw,
  Search,
  Filter,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { alertEngine, Alert, AlertRule } from '../utils/alertEngine';

interface SmartAlertsProps {
  onClose: () => void;
  wellId?: string;
  isDarkMode?: boolean;
}

export default function SmartAlerts({ onClose, wellId, isDarkMode = false }: SmartAlertsProps) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Detect dark mode from document if not passed as prop
  const darkMode = isDarkMode || document.documentElement.classList.contains('dark');

  useEffect(() => {
    // Subscribe to alert updates
    const unsubscribe = alertEngine.subscribe((newAlerts) => {
      setAlerts(wellId ? newAlerts.filter(a => a.wellId === wellId) : newAlerts);
    });

    // Load rules
    setRules(alertEngine.getRules());

    return unsubscribe;
  }, [wellId]);

  const filteredAlerts = alerts
    .filter(alert => {
      if (filterSeverity !== 'all' && alert.severity !== filterSeverity) return false;
      if (filterType !== 'all' && alert.type !== filterType) return false;
      if (!showAcknowledged && alert.acknowledged) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          alert.title.toLowerCase().includes(query) ||
          alert.message.toLowerCase().includes(query) ||
          alert.wellName.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by: unacknowledged first, then severity, then timestamp
      if (a.acknowledged !== b.acknowledged) return a.acknowledged ? 1 : -1;
      const severityOrder = { critical: 0, warning: 1, info: 2 };
      if (a.severity !== b.severity) return severityOrder[a.severity] - severityOrder[b.severity];
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical' && !a.acknowledged).length;
  const warningCount = alerts.filter(a => a.severity === 'warning' && !a.acknowledged).length;

  const handleAcknowledge = (alertId: string) => {
    alertEngine.acknowledgeAlert(alertId);
    toast.success('Alert acknowledged');
  };

  const handleAcknowledgeAll = () => {
    alertEngine.acknowledgeAllAlerts();
    toast.success('All alerts acknowledged');
  };

  const handleToggleRule = (ruleId: string, enabled: boolean) => {
    alertEngine.updateRule(ruleId, { enabled });
    setRules(alertEngine.getRules());
    toast.success(enabled ? 'Alert rule enabled' : 'Alert rule disabled');
  };

  const handleUpdateThreshold = (ruleId: string, threshold: number) => {
    alertEngine.updateRule(ruleId, { threshold });
    setRules(alertEngine.getRules());
    toast.success('Threshold updated');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    await new Promise(resolve => setTimeout(resolve, 500));
    setIsRefreshing(false);
    toast.success('Alerts refreshed');
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          bg: darkMode ? 'bg-red-950/80' : 'bg-red-50',
          border: darkMode ? 'border-red-800' : 'border-red-200',
          text: darkMode ? 'text-red-300' : 'text-red-700',
          badge: darkMode ? 'bg-red-900 text-red-200 border-red-700' : 'bg-red-100 text-red-800 border-red-300',
          icon: darkMode ? 'bg-red-900 text-red-300' : 'bg-red-500 text-white',
          ring: 'ring-red-500/40',
        };
      case 'warning':
        return {
          bg: darkMode ? 'bg-amber-950/80' : 'bg-amber-50',
          border: darkMode ? 'border-amber-800' : 'border-amber-200',
          text: darkMode ? 'text-amber-300' : 'text-amber-700',
          badge: darkMode ? 'bg-amber-900 text-amber-200 border-amber-700' : 'bg-amber-100 text-amber-800 border-amber-300',
          icon: darkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-500 text-white',
          ring: 'ring-amber-500/40',
        };
      case 'info':
      default:
        return {
          bg: darkMode ? 'bg-blue-950/80' : 'bg-blue-50',
          border: darkMode ? 'border-blue-800' : 'border-blue-200',
          text: darkMode ? 'text-blue-300' : 'text-blue-700',
          badge: darkMode ? 'bg-blue-900 text-blue-200 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-300',
          icon: darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-500 text-white',
          ring: 'ring-blue-500/40',
        };
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pressure-drop': return <TrendingDown className="h-5 w-5" />;
      case 'tank-level': return <Droplets className="h-5 w-5" />;
      case 'tank-full': return <Calendar className="h-5 w-5" />;
      case 'maintenance-due': return <Wrench className="h-5 w-5" />;
      case 'production-drop': return <TrendingDown className="h-5 w-5" />;
      case 'water-cut': return <Droplets className="h-5 w-5" />;
      default: return <Bell className="h-5 w-5" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 ${darkMode ? 'bg-slate-950' : 'bg-gray-50'}`}
      />

      {/* Main Container */}
      <div className="relative flex flex-col h-full w-full max-w-4xl mx-auto">
        {/* Header - Sticky */}
        <div className={`
          sticky top-0 z-20 flex-shrink-0
          ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}
          border-b safe-area-inset-top
        `}>
          <div className="flex items-center justify-between px-4 py-4">
            {/* Back Button & Title */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className={`
                  h-10 w-10 rounded-xl
                  ${darkMode
                    ? 'bg-slate-800 hover:bg-slate-700 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}
                `}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Alerts
                </h1>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {wellId ? 'For this well' : 'All active alerts'}
                </p>
              </div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`
                  h-10 w-10 rounded-xl
                  ${darkMode
                    ? 'bg-slate-800 hover:bg-slate-700 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}
                `}
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                className={`
                  h-10 w-10 rounded-xl
                  ${darkMode
                    ? 'bg-slate-800 hover:bg-slate-700 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}
                `}
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(!showSettings)}
                className={`
                  h-10 w-10 rounded-xl
                  ${showSettings
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : darkMode
                      ? 'bg-slate-800 hover:bg-slate-700 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}
                `}
              >
                <Settings className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="px-4 pb-4 grid grid-cols-3 gap-2">
            <div className={`
              rounded-xl p-3 text-center
              ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200 shadow-sm'}
            `}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {unacknowledgedCount}
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unread</p>
            </div>
            <div className={`
              rounded-xl p-3 text-center
              ${darkMode ? 'bg-red-950 border border-red-900' : 'bg-red-50 border border-red-200 shadow-sm'}
            `}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                {criticalCount}
              </p>
              <p className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Critical</p>
            </div>
            <div className={`
              rounded-xl p-3 text-center
              ${darkMode ? 'bg-amber-950 border border-amber-900' : 'bg-amber-50 border border-amber-200 shadow-sm'}
            `}>
              <p className={`text-2xl font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                {warningCount}
              </p>
              <p className={`text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Warning</p>
            </div>
          </div>
        </div>

        {/* Settings Panel (Collapsible) */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={`
                overflow-hidden flex-shrink-0 border-b
                ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-gray-50 border-gray-200'}
              `}
            >
              <div className="p-4">
                <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Settings className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  Alert Rules
                </h3>
                <div className="space-y-2">
                  {rules.map(rule => (
                    <Card
                      key={rule.id}
                      className={`
                        border
                        ${darkMode
                          ? 'bg-slate-800 border-slate-700'
                          : 'bg-white border-gray-200'}
                      `}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1">
                            <Switch
                              checked={rule.enabled}
                              onCheckedChange={(checked) => handleToggleRule(rule.id, checked)}
                            />
                            <div>
                              <p className={`font-medium text-sm capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {rule.type.replace(/-/g, ' ')}
                              </p>
                              {rule.threshold !== undefined && rule.enabled && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Threshold:
                                  </span>
                                  <Input
                                    type="number"
                                    value={rule.threshold}
                                    onChange={(e) => handleUpdateThreshold(rule.id, parseFloat(e.target.value))}
                                    className={`
                                      h-7 w-20 text-xs
                                      ${darkMode
                                        ? 'bg-slate-700 border-slate-600 text-white'
                                        : 'bg-gray-50 border-gray-200'}
                                    `}
                                  />
                                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {rule.type.includes('level') || rule.type.includes('cut') ? '%' :
                                      rule.type.includes('drop') ? '% change' : ''}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filters Section */}
        <div className={`
          flex-shrink-0 px-4 py-3 border-b
          ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}
        `}>
          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <Input
              type="text"
              placeholder="Search alerts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`
                pl-9 h-10 rounded-xl
                ${darkMode
                  ? 'bg-slate-800 border-slate-700 text-white placeholder:text-gray-500'
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}
              `}
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex gap-2 mb-3">
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className={`
                flex-1 h-10 px-3 rounded-xl text-sm border appearance-none cursor-pointer
                ${darkMode
                  ? 'bg-slate-800 border-slate-700 text-white'
                  : 'bg-white border-gray-200 text-gray-900'}
              `}
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`
                flex-1 h-10 px-3 rounded-xl text-sm border appearance-none cursor-pointer
                ${darkMode
                  ? 'bg-slate-800 border-slate-700 text-white'
                  : 'bg-white border-gray-200 text-gray-900'}
              `}
            >
              <option value="all">All Types</option>
              <option value="pressure-drop">Pressure Drop</option>
              <option value="tank-level">Tank Level</option>
              <option value="tank-full">Tank Full</option>
              <option value="maintenance-due">Maintenance</option>
              <option value="production-drop">Production Drop</option>
              <option value="water-cut">Water Cut</option>
            </select>
          </div>

          {/* Toggle & Bulk Action */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={showAcknowledged}
                onCheckedChange={setShowAcknowledged}
              />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Show acknowledged
              </span>
            </label>

            {unacknowledgedCount > 0 && (
              <Button
                onClick={handleAcknowledgeAll}
                size="sm"
                className="h-9 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Acknowledge All
              </Button>
            )}
          </div>
        </div>

        {/* Alerts List - Scrollable */}
        <div className={`
          flex-1 overflow-y-auto px-4 py-4
          ${darkMode ? 'bg-slate-950' : 'bg-gray-50'}
        `}>
          {filteredAlerts.length === 0 ? (
            <div className={`
              flex flex-col items-center justify-center h-full min-h-[300px]
              rounded-2xl border-2 border-dashed p-8
              ${darkMode
                ? 'bg-slate-900 border-slate-700'
                : 'bg-white border-gray-200'}
            `}>
              <div className={`
                w-16 h-16 rounded-full flex items-center justify-center mb-4
                ${darkMode ? 'bg-slate-800' : 'bg-gray-100'}
              `}>
                <BellOff className={`h-8 w-8 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              </div>
              <p className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                No alerts
              </p>
              <p className={`text-sm text-center max-w-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {showAcknowledged
                  ? 'No alerts match your current filters.'
                  : 'All alerts have been acknowledged. Enjoy the calm.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAlerts.map((alert, index) => {
                const styles = getSeverityStyles(alert.severity);
                const isExpanded = expandedAlert === alert.id;

                return (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`
                      border overflow-hidden
                      ${alert.acknowledged ? 'opacity-60' : ''}
                      ${styles.bg} ${styles.border}
                      ${!alert.acknowledged ? `ring-1 ${styles.ring}` : ''}
                    `}>
                      <CardContent className="p-4">
                        {/* Alert Header */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className={`
                              flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                              ${styles.icon}
                            `}>
                              {getTypeIcon(alert.type)}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center flex-wrap gap-2 mb-1">
                                <h3 className={`font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {alert.title}
                                </h3>
                                <Badge className={`${styles.badge} text-xs border`}>
                                  {alert.severity}
                                </Badge>
                              </div>
                              <p className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {alert.wellName}
                              </p>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {alert.message}
                              </p>

                              {/* Meta Info */}
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {formatTimestamp(alert.timestamp)}
                                </span>
                                {alert.actionRequired && (
                                  <Badge variant="outline" className={`
                                    text-xs
                                    ${darkMode
                                      ? 'bg-orange-950 text-orange-300 border-orange-700'
                                      : 'bg-orange-50 text-orange-700 border-orange-200'}
                                  `}>
                                    <Zap className="h-3 w-3 mr-1" />
                                    Action Required
                                  </Badge>
                                )}
                                {alert.acknowledged && (
                                  <Badge variant="outline" className={`
                                    text-xs
                                    ${darkMode
                                      ? 'bg-green-950 text-green-300 border-green-700'
                                      : 'bg-green-50 text-green-700 border-green-200'}
                                  `}>
                                    <Check className="h-3 w-3 mr-1" />
                                    Acknowledged
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {alert.data && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                                className={`
                                  h-9 w-9 rounded-lg
                                  ${darkMode
                                    ? 'hover:bg-slate-700 text-gray-400'
                                    : 'hover:bg-gray-100 text-gray-500'}
                                `}
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                            )}
                            {!alert.acknowledged && (
                              <Button
                                onClick={() => handleAcknowledge(alert.id)}
                                size="sm"
                                className={`
                                  h-9 rounded-lg
                                  ${darkMode
                                    ? 'bg-slate-700 hover:bg-slate-600 text-white border-slate-600'
                                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'}
                                  border
                                `}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Ack
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        <AnimatePresence>
                          {isExpanded && alert.data && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className={`
                                mt-4 p-4 rounded-xl border
                                ${darkMode
                                  ? 'bg-slate-800 border-slate-700'
                                  : 'bg-white border-gray-200'}
                              `}>
                                <p className={`text-xs font-semibold mb-3 uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Additional Details
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                  {Object.entries(alert.data).map(([key, value]) => (
                                    <div key={key} className="flex flex-col">
                                      <span className={`text-xs uppercase tracking-wide ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {key.replace(/([A-Z])/g, ' $1')}
                                      </span>
                                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                        {typeof value === 'number' ? value.toFixed(2) : String(value)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile Close Button - Fixed at Bottom */}
        <div className={`
          flex-shrink-0 p-4 border-t safe-area-inset-bottom
          ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}
          md:hidden
        `}>
          <Button
            onClick={onClose}
            className={`
              w-full h-12 rounded-xl font-semibold text-base
              ${darkMode
                ? 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-200'}
              border
            `}
          >
            <X className="h-5 w-5 mr-2" />
            Close Alerts
          </Button>
        </div>
      </div>
    </div>
  );
}
